import React, { useMemo, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";

import { useTypedSelector } from "../../../../redux/store";
import useActiveTab from "../../../../hooks/useActiveTab";
import { hasAccess } from "../../../../access";
import { TabOptions } from "../../../../types";
import BasicPageLayout from "../../../../components/BasicPageLayout";
import { SuspenseLoader, SideTab } from "../../../../components/common";

import { TabKeys, TabAccessNames } from "./constants/access";

const LazyCashlessOrders = lazy<React.FC<AccountingReportsPage.Props>>(
	async () => {
		const elem = await import(
			"./tabs/CashlessOrders" /* webpackChunkName: "reports-accounting-reports-cashless-orders" */
		);
		return { default: elem.default };
	},
);
const LazyCashlessOrdersPeriodReport = lazy<
	React.FC<AccountingReportsPage.Props>
>(async () => {
	const elem = await import(
		"./tabs/CashlessOrdersPeriodReport" /* webpackChunkName: "reports-accounting-reports-page-cashless-orders-period-report" */
	);
	return { default: elem.default };
});
const LazyFuelDeductionOrdersReport = lazy<
	React.FC<AccountingReportsPage.Props>
>(async () => {
	const elem = await import(
		"./tabs/FuelDeductionOrdersReport" /* webpackChunkName: "reports-accounting-reports-page-fuel-deduction-orders-report" */
	);
	return { default: elem.default };
});
const LazyAgentOrdersReport = lazy<React.FC<AccountingReportsPage.Props>>(
	async () => {
		const elem = await import(
			"./tabs/AgentOrdersReport" /* webpackChunkName: "reports-accounting-reports-page-agent-orders-report" */
		);
		return { default: elem.default };
	},
);
const LazyAccountsReport = lazy<React.FC<AccountingReportsPage.Props>>(
	async () => {
		const elem = await import(
			"./tabs/AccountsReport" /* webpackChunkName: "reports-accounting-reports-page-accounts-report" */
		);
		return { default: elem.default };
	},
);

const AccountingReportsPage: React.FC = () => {
	const { t } = useTranslation();

	const personalRole = useTypedSelector(
		(state) => state.account.personalRole,
	);

	const tabs: TabOptions.Array = useMemo(
		() =>
			[
				{
					key: TabKeys.CASHLESS_ORDERS,
					label: t(
						"mainPage.reports.accounting.cashlessOrders.title",
					),
					value: {
						render: () => (
							<Suspense fallback={<SuspenseLoader />}>
								<LazyCashlessOrders
									headerTitle={t(
										"mainPage.reports.accounting.cashlessOrders.title",
									)}
								/>
							</Suspense>
						),
					},
					accessName: TabAccessNames[TabKeys.CASHLESS_ORDERS],
				},
				{
					key: TabKeys.CASHLESS_ORDERS_GENERAL,
					label: t(
						"mainPage.reports.accounting.cashlessOrdersGeneral.title",
					),
					value: {
						render: () => (
							<Suspense fallback={<SuspenseLoader />}>
								<LazyCashlessOrders
									headerTitle={t(
										"mainPage.reports.accounting.cashlessOrdersGeneral.title",
									)}
									general={true}
								/>
							</Suspense>
						),
					},
					accessName: TabAccessNames[TabKeys.CASHLESS_ORDERS_GENERAL],
				},
				{
					key: TabKeys.CASHLESS_ORDERS_PERIOD_REPORT,
					label: t(
						"mainPage.reports.accounting.cashlessOrdersPeriodReport.title",
					),
					value: {
						render: () => (
							<Suspense fallback={<SuspenseLoader />}>
								<LazyCashlessOrdersPeriodReport
									headerTitle={t(
										"mainPage.reports.accounting.cashlessOrdersPeriodReport.title",
									)}
								/>
							</Suspense>
						),
					},
					accessName:
						TabAccessNames[TabKeys.CASHLESS_ORDERS_PERIOD_REPORT],
				},
				{
					key: TabKeys.CASHLESS_ACCOUNTS_SUMMARY_REPORT,
					label: t(
						"mainPage.reports.accounting.cashlessAccountsSummaryReport.title",
					),
					value: {
						render: () => (
							<Suspense fallback={<SuspenseLoader />}>
								<LazyCashlessOrdersPeriodReport
									headerTitle={t(
										"mainPage.reports.accounting.cashlessAccountsSummaryReport.title",
									)}
									general={true}
								/>
							</Suspense>
						),
					},
					accessName:
						TabAccessNames[
							TabKeys.CASHLESS_ACCOUNTS_SUMMARY_REPORT
						],
				},
				{
					key: TabKeys.FUEL_DEDUCTION_ORDERS_REPORT,
					label: t(
						"mainPage.reports.accounting.fuelDeductionOrdersReport.title",
					),
					value: {
						render: () => (
							<Suspense fallback={<SuspenseLoader />}>
								<LazyFuelDeductionOrdersReport
									headerTitle={t(
										"mainPage.reports.accounting.fuelDeductionOrdersReport.title",
									)}
								/>
							</Suspense>
						),
					},
					accessName:
						TabAccessNames[TabKeys.FUEL_DEDUCTION_ORDERS_REPORT],
				},
				{
					key: TabKeys.AGENT_ORDERS_REPORT,
					label: t(
						"mainPage.reports.accounting.agentOrdersReport.title",
					),
					value: {
						render: () => (
							<Suspense fallback={<SuspenseLoader />}>
								<LazyAgentOrdersReport
									headerTitle={t(
										"mainPage.reports.accounting.agentOrdersReport.title",
									)}
								/>
							</Suspense>
						),
					},
					accessName: TabAccessNames[TabKeys.AGENT_ORDERS_REPORT],
				},
				{
					key: TabKeys.ACCOUNTS_REPORT,
					label: t(
						"mainPage.reports.accounting.accountsReport.title",
					),
					value: {
						render: () => (
							<Suspense fallback={<SuspenseLoader />}>
								<LazyAccountsReport
									headerTitle={t(
										"mainPage.reports.accounting.accountsReport.title",
									)}
								/>
							</Suspense>
						),
					},
					accessName: TabAccessNames[TabKeys.ACCOUNTS_REPORT],
				},
			].filter((btn) => hasAccess(btn.accessName, personalRole)),
		[t, personalRole],
	);

	const { activeKey, activeTab, setActiveKey } = useActiveTab(tabs);

	return (
		<BasicPageLayout
			navigation={
				<SideTab
					value={activeKey}
					onChange={(newActive) => setActiveKey(newActive)}
					options={tabs}
					variant="vertical"
				/>
			}
			content={activeTab.value.render()}
		/>
	);
};

declare namespace AccountingReportsPage {
	interface Props {
		headerTitle: string;
		general?: boolean;
	}
}

export default AccountingReportsPage;
